import { Trans, t } from '@lingui/macro';
import { XCircle, Grid, List as ListIcon, Download, AlertCircle, FileText, Lock, ExternalLink } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { GetExternalShareInfoResponse } from '../../models/DTO';
import { RoomItem } from '../../models/RoomItem';
import { Fire } from '../../services/FireClient';
import { My } from '../../services/My';
import { UtilApp } from '../../services/Utils/UtilApp';
import { MouseOverPreview } from '../components/ItemFilePopover';
import { FileIconThumb } from '../components/MessageFileItem';
import { ViewMode } from '../rooms/right-menu/RoomFilesModal';
import { Util } from '../../services/Utils/Util';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Progress } from '../../components/ui/progress';
import { useToast } from '../../components/ui/use-toast';
import { Separator } from '../../components/ui/separator';
import { cn } from '../../lib/utils';
import { FileIcon } from '../components/MessageFileItem';

const NEXTPOD_HOMEPAGE = 'https://nextpod.jp';

const ExternalPublicSharePage: React.FC = () => {
  const [inProgress, setInProgress] = useState(false);
  const { toast } = useToast();
  const { linkId } = useParams<{ linkId: string; }>();
  const [result, setResult] = useState<GetExternalShareInfoResponse | null>(null);
  const [pass, setPass] = useState<string | null>(null);
  const [downloadingItemIds, setDownloadingItemIds] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [downloadCounts, setDownloadCounts] = useState<Record<string, number>>({});
  const numOfImages = result?.link?.roomItems.filter(item => Util.isImageUrl(item.url)).length;
  const numOfFiles = result?.link?.roomItems.filter(item => !Util.isImageUrl(item.url)).length;

  useEffect(() => {
    setPass(null);
    reloadLink();
  }, [linkId])

  useEffect(() => {
    // Disable right menu to avoid copy urls or download link.
    document.addEventListener('contextmenu', event => {
      event.preventDefault();
    });

    // reload this page every 29 minutes to keep the links valid.
    const i = setInterval(() => {
      reloadLink();
    }, 29 * 60 * 1000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    setResultViewMode();
  }, [numOfImages, numOfFiles]);

  async function reloadLink() {
    setInProgress(true);
    setResult(null);
    setDownloadingItemIds([]);
    try {
      let resp = await Fire.item.getExternalShareInfo(linkId || "", pass);
      console.log("External public share link info:", resp);
      setResult(resp);
      setDownloadCounts(resp.downloadCounts ?? {});
    } catch (error) {
      toast({
        variant: "destructive",
        description: (error as Error).message,
      });
    }
    setInProgress(false);
  }

  function setResultViewMode() {
    if (numOfImages && (numOfFiles ?? 0) && numOfImages >= (numOfFiles ?? 0)) {
      setViewMode('grid');
    } else {
      setViewMode('list');
    }
  }

  // Header Component with NEXTPOD branding
  const PageHeader = () => (
    <header className="bg-white sticky top-0 z-40">
      <div className="w-full max-w-4xl mx-auto h-14 flex items-center">
        <a
          href={NEXTPOD_HOMEPAGE}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
        >
          <img src="/assets/icon/icon-192.png" className="w-8 h-8 rounded-lg" alt="NEXTPOD" />
          <span className="font-bold text-lg text-gray-900">NEXTPOD</span>
        </a>
      </div>
    </header>
  );

  return (
    <div className="bg-white h-full flex flex-col overflow-y-auto">
      <PageHeader />

      {inProgress && (
        <div className="fixed inset-0 bg-white/50 z-50 flex items-center justify-center">
          <div className="w-[60%] max-w-sm flex flex-col items-center gap-4">
            <img src="/assets/img/img-cover-hello-160.svg" className="w-40 animate-pulse" alt="Loading" />
            <Progress value={undefined} className="w-full" />
          </div>
        </div>
      )}

      <main className="flex-1 flex flex-col items-center pb-8 px-4">
        {result && <ResultView />}
      </main>

    </div>
  );

  function ResultView() {
    if (!result) return null;

    if (result.status === 'EXPIRED') {
      return (
        <Card className="max-w-md w-full shadow-lg border-0">
          <CardContent className="flex flex-col items-center justify-center text-center p-8">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2"><Trans>Link Expired</Trans></h1>
            <p className="text-gray-600"><Trans>This file sharing link has expired</Trans></p>
          </CardContent>
        </Card>
      );
    }

    if (result.status.includes('PASSWORD')) {
      return (
        <Card className="max-w-md w-full mx-auto shadow-lg border-0">
          <CardContent className="flex flex-col items-center text-center pt-8 pb-8 px-6 space-y-4">
            <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center">
              <Lock className="w-8 h-8 text-orange-600" />
            </div>
            <div className="space-y-1">
              <h2 className="text-xl font-bold text-gray-900"><Trans>Protected Files</Trans></h2>
              <p className="text-sm text-gray-500"><Trans>Enter the password to access shared files</Trans></p>
            </div>

            <div className="w-full space-y-4 pt-4">
              <Input
                type="password"
                placeholder={t`Enter password`}
                value={pass ?? ''}
                onChange={(e) => setPass(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') reloadLink();
                }}
                className={cn("text-center h-12", result.status === 'PASSWORD_INCORRECT' && "border-red-500 focus-visible:ring-red-500")}
              />

              {result.status === 'PASSWORD_INCORRECT' && (
                <p className="text-sm text-red-500 font-medium"><Trans>Incorrect password. Please try again.</Trans></p>
              )}

              <Button onClick={reloadLink} className="w-full h-11 bg-orange-600 hover:bg-orange-700">
                <Trans>Access Files</Trans>
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }

    if (result.link) {
      const workspaceName = result.link.workspaceName;
      const roomName = result.link.roomName;
      const roomProfileImageUrl = result.link.roomProfileImageUrl;
      const totalFiles = result.link.roomItems.length;
      const totalSize = result.link.roomItems.reduce((acc, item) => acc + (item.file?.sizeByte ?? 0), 0);
      const roomInitial = (roomName || workspaceName || 'F').charAt(0).toUpperCase();

      return (
        <Card className="w-full max-w-4xl mx-auto shadow-lg border-0 overflow-hidden">
          <CardContent className="p-0">
            {/* Hero Header */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6 text-white">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl bg-white shadow-md flex items-center justify-center shrink-0 overflow-hidden">
                  {roomProfileImageUrl ? (
                    <img src={roomProfileImageUrl} className="w-full h-full object-cover" alt="" />
                  ) : (
                    <span className="text-2xl font-bold text-orange-500">{roomInitial}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-xl font-bold mb-1 truncate">
                    {workspaceName || roomName || <Trans>Shared Files</Trans>}
                  </h1>
                  {workspaceName && roomName && (
                    <p className="text-white/80 text-sm truncate">{roomName}</p>
                  )}
                  <p className="text-white/70 text-sm mt-2">
                    <Trans>Shared by</Trans> <span className="font-medium text-white">{result.link.user.name}</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Stats Bar */}
            <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">
                    {totalFiles} <Trans>files</Trans>
                    {totalSize > 0 && <span className="text-gray-400 ml-1">({Util.niceBytes(totalSize)})</span>}
                  </span>
                </div>
                {result.link.expireDate && (
                  <div className="text-gray-500">
                    <Trans>Expires</Trans>: <span className="font-medium">{UtilApp.dateStrToTimeAndDay(result.link.expireDate, My.user.lang)}</span>
                  </div>
                )}
                {result.link.maxDownloads && (
                  <div className="text-gray-500">
                    <Trans>Max downloads</Trans>: <span className="font-medium">{result.link.maxDownloads}</span>
                  </div>
                )}
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center p-1 bg-white rounded-lg border border-gray-200 shadow-sm">
                <button
                  onClick={() => setViewMode('list')}
                  className={cn(
                    "flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-all",
                    viewMode === 'list'
                      ? "bg-orange-100 text-orange-700"
                      : "text-gray-500 hover:text-gray-700"
                  )}
                >
                  <ListIcon className="w-4 h-4" />
                  <span className="hidden sm:inline"><Trans>List</Trans></span>
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={cn(
                    "flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-all",
                    viewMode === 'grid'
                      ? "bg-orange-100 text-orange-700"
                      : "text-gray-500 hover:text-gray-700"
                  )}
                >
                  <Grid className="w-4 h-4" />
                  <span className="hidden sm:inline"><Trans>Grid</Trans></span>
                </button>
              </div>
            </div>

            {/* Files Grid/List */}
            <div className="p-4 md:p-6 bg-white min-h-[300px]">
              <div className={cn("w-full", viewMode === 'grid' ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4' : 'flex flex-col gap-2')}>
                {result.link.roomItems.map(item => getFileShareItem(item))}
              </div>
            </div>

            {/* Bottom Info */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-100">
              <div className="text-xs text-gray-500 text-center">
                <Trans>Last updated</Trans>: {UtilApp.dateStrToTimeAndDay(result.link.updated, My.user.lang)}
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    return null;
  }

  function getFileShareItem(item: RoomItem) {
    const fileSizeBytes = item.file?.sizeByte;
    const fileSizeLabel = fileSizeBytes ? Util.niceBytes(fileSizeBytes) : null;
    const maxDownloadsPerFile = result?.link?.maxDownloads ?? null;
    const downloadsCount = downloadCounts[item.id] ?? 0;
    const reachedLimit = !!maxDownloadsPerFile && downloadsCount >= maxDownloadsPerFile;
    const hasDownloadSource = !!item.storagePath || !!item.url;
    const isDownloadable = hasDownloadSource && !reachedLimit;
    const isDownloading = downloadingItemIds.includes(item.id);
    const countLabel = maxDownloadsPerFile ? `${downloadsCount}/${maxDownloadsPerFile}` : `${downloadsCount}`;

    const showDisabledToast = () => toast({
      description: reachedLimit ? t`Max downloads reached` : t`File unavailable`,
      variant: 'destructive',
    });

    const handleClick = () => {
      if (isDownloadable) {
        handleDownload(item);
      } else {
        showDisabledToast();
      }
    };

    if (viewMode === 'grid') {
      return (
        <div key={item.id} className="relative group flex flex-col items-center p-3 rounded-xl border border-gray-100 hover:border-orange-300 hover:shadow-md transition-all cursor-pointer bg-white" onClick={handleClick}>
          <div className="w-full aspect-square flex items-center justify-center mb-2 overflow-hidden rounded-lg bg-gray-50">
            {Util.isImageUrl(item.url) ? (
              <img src={item.url} alt={item.title || ''} className="w-full h-full object-cover" />
            ) : (
              <div className="scale-150 transform"><FileIcon filename={item.title ?? ''} /></div>
            )}
          </div>
          <p className="text-xs text-center font-medium text-gray-700 w-full truncate px-1" title={item.title || ''}>{item.title}</p>
          <div className="flex items-center gap-2 mt-2">
            {fileSizeLabel && <span className="text-[10px] text-gray-400">{fileSizeLabel}</span>}
            <code className={cn("text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500", reachedLimit && "bg-red-50 text-red-500")}>
              {countLabel}
            </code>
          </div>
          {isDownloading && (
            <div className="absolute inset-0 bg-white/60 flex items-center justify-center rounded-xl">
              <div className="animate-spin w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full" />
            </div>
          )}
        </div>
      );
    }

    // ViewMode 'list'
    return (
      <div key={item.id} className="w-full">
        <MouseOverPreview imageUrl={item.url} content={() => (
          <div
            onClick={handleClick}
            className={cn(
              "flex items-center gap-3 p-3 rounded-lg border border-transparent hover:border-orange-200 hover:bg-orange-50/50 cursor-pointer transition-colors relative",
              !isDownloadable && "opacity-60 cursor-not-allowed"
            )}
          >
            <div className="shrink-0">
              <FileIconThumb filename={item.title ?? ''} url={item.url ?? ''} />
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{item.title}</p>
              {fileSizeLabel && <p className="text-xs text-gray-500">{fileSizeLabel}</p>}
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <div className={cn("text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 font-mono", reachedLimit && "bg-red-50 text-red-600")}>
                {countLabel}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-gray-500 hover:text-orange-600 hover:bg-orange-100"
                disabled={!isDownloadable || isDownloading}
                onClick={(e) => { e.stopPropagation(); handleClick(); }}
              >
                {isDownloading ? (
                  <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        )} />
      </div>
    );
  }

  async function handleDownload(item: RoomItem) {
    if (downloadingItemIds.includes(item.id)) {
      return;
    }

    const maxDownloadsPerFile = result?.link?.maxDownloads ?? null;
    const currentCount = downloadCounts[item.id] ?? 0;
    const reachedLimit = !!maxDownloadsPerFile && currentCount >= maxDownloadsPerFile;

    if (reachedLimit) {
      toast({ description: t`Max downloads reached`, variant: 'destructive' });
      return;
    }

    setDownloadingItemIds(prev => [...prev, item.id]);
    try {
      const response = await Fire.item.getExternalShareURL(linkId || "", item.id);
      if (response.status === 'SUCCESS' && response.url) {
        window.open(response.url, '_blank');
        setDownloadCounts(prev => ({ ...prev, [item.id]: response.downloads }));
      } else if (response.status === 'MAX_DOWNLOADS_REACHED') {
        setDownloadCounts(prev => ({ ...prev, [item.id]: response.downloads }));
        toast({ description: t`Max downloads reached`, variant: 'destructive' });
      } else {
        toast({ description: t`File unavailable`, variant: 'destructive' });
      }
    } catch (error) {
      console.error('Failed to download shared file', error);
      toast({ description: (error as Error).message, variant: "destructive" });
    } finally {
      setDownloadingItemIds(prev => prev.filter(id => id !== item.id));
    }
  }
};

export default ExternalPublicSharePage;
